
import React, { useEffect, useRef } from 'react';
import { Check, Loader2, ExternalLink, Shield, RefreshCw, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { TradingAccountSelect } from './connect-api-dialog/TradingAccountSelect';
import { useSimplifiedConnectApiForm } from '@/hooks/use-simplified-connect-api-form';
import { useIsMobile } from '@/hooks/use-mobile';

interface SimplifiedConnectApiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cspAccountId: string;
  onSubmit?: (data: any) => Promise<void>;
}

export function SimplifiedConnectApiDialog({
  open,
  onOpenChange,
  cspAccountId,
  onSubmit
}: SimplifiedConnectApiDialogProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const hasInitialized = useRef(false);
  const hasResetOnOpen = useRef(false);
  
  const {
    form,
    isLoading,
    isAuthenticating,
    authenticated,
    tradingAccounts,
    selectedAccount,
    canConnect,
    currentError,
    canRetry,
    handleAuthenticate,
    handleRetry,
    handleSubmit,
    resetForm
  } = useSimplifiedConnectApiForm({
    cspAccountId,
    onSuccess: () => onOpenChange(false)
  });

  // Smart reset with enhanced UX
  useEffect(() => {
    if (open && !hasResetOnOpen.current) {
      const savedResult = localStorage.getItem('ctrader_oauth_result');
      const hasValidTokens = authenticated && tradingAccounts.length > 0;
      
      if (!hasValidTokens && !savedResult) {
        localStorage.removeItem('ctrader_oauth_result');
        localStorage.removeItem('ctrader_form_data');
        localStorage.removeItem('ctrader_connection_id');
        localStorage.setItem('ctrader_return_path', window.location.pathname + window.location.search);
        resetForm();
      } else {
        localStorage.setItem('ctrader_return_path', window.location.pathname + window.location.search);
      }
      
      hasInitialized.current = true;
      hasResetOnOpen.current = true;
    }
    
    if (!open) {
      hasInitialized.current = false;
      hasResetOnOpen.current = false;
    }
  }, [open, resetForm, authenticated, tradingAccounts.length]);

  const watchTradingAccountId = form.watch('trading_account_id');

  // Enhanced progress calculation for better UX
  const getProgressValue = () => {
    if (!authenticated) return 0;
    if (authenticated && !watchTradingAccountId) return 50;
    if (authenticated && watchTradingAccountId && !isLoading) return 75;
    if (isLoading) return 90;
    return 100;
  };

  // Quick fix suggestions based on error type
  const getQuickFixSuggestions = (error: any) => {
    if (!error) return [];
    
    const suggestions = [];
    
    if (error.category === 'network' || error.message?.includes('network')) {
      suggestions.push({
        icon: Wifi,
        title: 'Check Connection',
        description: 'Verify your internet connection',
        action: () => window.location.reload()
      });
    }
    
    if (error.category === 'timeout') {
      suggestions.push({
        icon: RefreshCw,
        title: 'Refresh Page',
        description: 'Reload the page and try again',
        action: () => window.location.reload()
      });
    }
    
    if (error.category === 'security' || error.message?.includes('CSRF')) {
      suggestions.push({
        icon: Shield,
        title: 'Clear Cache',
        description: 'Clear browser data and retry',
        action: () => {
          localStorage.clear();
          window.location.reload();
        }
      });
    }
    
    return suggestions;
  };

  // Enhanced error state with quick fixes
  const renderErrorState = () => {
    if (!currentError) return null;

    const isSecurityError = currentError.category === 'security';
    const isRateLimitError = currentError.message?.includes('Rate limited') || currentError.message?.includes('Too many attempts');
    const isNetworkError = currentError.category === 'network';
    
    let errorTitle = currentError.userMessage;
    let errorDescription = currentError.actionable;
    
    if (isSecurityError) {
      errorTitle = 'Security Validation Failed';
      errorDescription = 'This can happen if your browser cleared temporary data. Please try connecting again.';
    } else if (isRateLimitError) {
      errorTitle = 'Too Many Attempts';
      errorDescription = 'Please wait before trying again. This helps protect against unauthorized access.';
    } else if (isNetworkError) {
      errorTitle = 'Connection Issue';
      errorDescription = 'Please check your internet connection and try again.';
    }

    const quickFixes = getQuickFixSuggestions(currentError);

    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-3">
              <div>
                <p className="font-medium">{errorTitle}</p>
                <p className="text-sm mt-1">{errorDescription}</p>
              </div>
              
              {isSecurityError && (
                <p className="text-xs text-muted-foreground">
                  Don't worry - this is a security feature working as intended. Your data is safe.
                </p>
              )}
              
              {isRateLimitError && (
                <p className="text-xs text-muted-foreground">
                  Rate limiting helps protect your account from unauthorized access attempts.
                </p>
              )}
            </div>
          </AlertDescription>
        </Alert>

        {/* Quick Fix Suggestions */}
        {quickFixes.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Quick fixes:</p>
            <div className="grid gap-2">
              {quickFixes.map((fix, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={fix.action}
                  className="justify-start h-auto p-3"
                >
                  <fix.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <div className="text-left">
                    <p className="font-medium">{fix.title}</p>
                    <p className="text-xs text-muted-foreground">{fix.description}</p>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Regular retry button */}
        {canRetry && !isRateLimitError && (
          <Button
            type="button"
            onClick={handleRetry}
            disabled={isAuthenticating}
            variant="outline"
            className="w-full"
          >
            {isAuthenticating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Retrying... (60s timeout)
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  // Enhanced loading state with skeleton
  const renderLoadingState = () => (
    <div className="space-y-4">
      <div className="text-center space-y-3">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Loader2 className="h-8 w-8 text-green-600 animate-spin" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">Loading Trading Accounts</h3>
          <p className="text-sm text-gray-600">Please wait while we fetch your accounts...</p>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="space-y-2">
        <Progress value={getProgressValue()} className="h-2" />
        <p className="text-xs text-center text-gray-500">
          {getProgressValue() < 50 ? 'Authenticating...' : 
           getProgressValue() < 75 ? 'Loading accounts...' : 
           'Almost done...'}
        </p>
      </div>
      
      {/* Skeleton for accounts */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded-lg"></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isMobile ? 'max-w-[95vw] w-full h-[90vh]' : 'max-w-md w-full'} bg-white border-0 shadow-2xl rounded-xl overflow-hidden p-0`}>
        {/* Enhanced header */}
        <DialogHeader className="p-6 pb-0 space-y-4">
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 flex-shrink-0 overflow-hidden rounded-lg">
              <img 
                src="/lovable-uploads/c7f9c69f-f452-4d69-bb1d-4d4ca77e45dd.png" 
                alt="cTrader logo" 
                className="h-full w-full object-contain"
                draggable={false}
              />
            </div>
            <DialogTitle className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-900`}>
              Connect to cTrader
            </DialogTitle>
          </div>
          
          {/* Enhanced security badge with connection status */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Shield className="h-4 w-4 text-green-600" />
            <span>Enhanced Security & Rate Protection</span>
            {navigator.onLine ? (
              <Wifi className="h-3 w-3 text-green-600" />
            ) : (
              <WifiOff className="h-3 w-3 text-red-600" />
            )}
          </div>

          {/* Progress indicator for mobile */}
          {isMobile && (authenticated || isAuthenticating) && (
            <div className="space-y-2">
              <Progress value={getProgressValue()} className="h-1" />
              <p className="text-xs text-center text-gray-500">
                Step {authenticated ? '2' : '1'} of 2
              </p>
            </div>
          )}
        </DialogHeader>

        <div className={`px-6 pb-6 ${isMobile ? 'overflow-y-auto flex-1' : ''}`}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              
              {/* Show error state first if there's an error */}
              {currentError && renderErrorState()}
              
              {!authenticated && !currentError ? (
                <>
                  {/* Steps section - enhanced for mobile */}
                  <div className="space-y-4">
                    <h3 className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium text-gray-900 mb-3`}>How to connect:</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className={`${isMobile ? 'w-5 h-5 text-xs' : 'w-6 h-6 text-xs'} bg-tradebrand-green text-white rounded-full flex items-center justify-center font-medium mt-0.5`}>
                          1
                        </div>
                        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>Click "Connect to cTrader" button</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className={`${isMobile ? 'w-5 h-5 text-xs' : 'w-6 h-6 text-xs'} bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-medium mt-0.5`}>
                          2
                        </div>
                        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>Login to your cTrader account</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className={`${isMobile ? 'w-5 h-5 text-xs' : 'w-6 h-6 text-xs'} bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-medium mt-0.5`}>
                          3
                        </div>
                        <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>Select your trading account</p>
                      </div>
                    </div>
                  </div>

                  {/* Connect button with mobile optimization */}
                  <Button
                    type="button"
                    onClick={handleAuthenticate}
                    disabled={isAuthenticating}
                    className={`w-full ${isMobile ? 'h-14 text-base' : 'h-12'} bg-tradebrand-green hover:bg-tradebrand-green/90 text-white font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${isMobile ? 'active:scale-95' : ''}`}
                  >
                    {isAuthenticating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Connecting... (60s timeout)
                      </>
                    ) : (
                      <>
                        Connect to cTrader
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </>
              ) : authenticated && !currentError ? (
                <>
                  {/* Success state */}
                  <div className="text-center space-y-4">
                    <div className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} bg-green-100 rounded-full flex items-center justify-center mx-auto`}>
                      <Check className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-green-600`} />
                    </div>
                    
                    <div>
                      <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900 mb-2`}>
                        Successfully Connected!
                      </h3>
                      <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
                        {tradingAccounts.length > 0 
                          ? `Found ${tradingAccounts.length} trading account${tradingAccounts.length > 1 ? 's' : ''}`
                          : 'No trading accounts found'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Account selection with enhanced loading */}
                  {isLoading ? renderLoadingState() : tradingAccounts.length > 0 ? (
                    <div className="space-y-4">
                      <TradingAccountSelect
                        form={form}
                        tradingAccounts={tradingAccounts}
                        isLoading={false}
                      />
                      
                      {/* Final connect button - mobile optimized */}
                      <Button 
                        type="submit"
                        disabled={isLoading || !canConnect}
                        className={`w-full ${isMobile ? 'h-14 text-base' : 'h-12'} bg-tradebrand-green hover:bg-tradebrand-green/90 text-white font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${isMobile ? 'active:scale-95' : ''}`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Connecting Account...
                          </>
                        ) : (
                          'Complete Connection'
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 mb-4`}>
                        No trading accounts found on your cTrader profile.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => window.open('https://ctrader.com/open-account/', '_blank')}
                        className={`${isMobile ? 'text-xs h-10' : 'text-sm'}`}
                      >
                        Create Trading Account
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              ) : null}
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
